/**
 * CareValidate Product Configuration with Environment-based Product Bundle IDs
 * 
 * Returns product configurations with productBundleId values that differ
 * between staging and production environments.
 * 
 * Environment Detection:
 * - Uses VITE_CAREVALIDATE_API_URL to determine environment
 * - Staging: api-staging.care360-next.carevalidate.com
 * - Production: api.care360-next.carevalidate.com
 * 
 * Note: Currently using the same bundle IDs for both environments.
 */

/**
 * Get products configuration with environment-specific productBundleId
 * @returns {Object} Product catalog configuration
 */
export function getCvProductsConfig() {
  const isProduction = import.meta.env.VITE_CAREVALIDATE_API_URL?.includes('api.care360-next.carevalidate.com')
  
  return {
    product_1: {
      id: "product_1",
      name: "Semaglutide Injection",
      display_name: "Semaglutide",
      another_name: "Semaglutide Injection",
      type: "injection",
      medication: "semaglutide",
      isVisible: true,
      price: 289.00,
      productId: isProduction 
      ? "d1f1479a-4750-4445-8273-ec894e37bdc6" // Production product ID
      : "d1f1479a-4750-4445-8273-ec894e37bdc6", // Staging product ID
      productBundleId: isProduction 
        ? "021802a1-a4f2-4586-95aa-a32a2ee7a0e0" // Production bundle ID
        : "021802a1-a4f2-4586-95aa-a32a2ee7a0e0", // Staging bundle ID
      imageUrl: "/images/products/semaglutide_bottle-01x01.png",
      imageUrl2: "/images/products/semaglutide-plan-v2.png",
      pricePlan: {
        monthly: {
          months: 1,
          price: 289.00,
          pricePerMonth: 289.00,
          productId: isProduction 
            ? "d1f1479a-4750-4445-8273-ec894e37bdc6" // Production product ID
            : "d1f1479a-4750-4445-8273-ec894e37bdc6", // Staging product ID
          productBundleId: isProduction 
            ? "021802a1-a4f2-4586-95aa-a32a2ee7a0e0" // Production bundle ID
            : "021802a1-a4f2-4586-95aa-a32a2ee7a0e0", // Staging bundle ID
        },
        quarterly: {
          months: 3,
          price: 575.00,
          pricePerMonth: 191.67,
          productId: isProduction 
            ? "d1f1479a-4750-4445-8273-ec894e37bdc6" // Production product ID
            : "d1f1479a-4750-4445-8273-ec894e37bdc6", // Staging product ID
          productBundleId: isProduction 
            ? "9ed1b68f-10f2-4510-89da-97362f1974b9" // Production bundle ID
            : "9ed1b68f-10f2-4510-89da-97362f1974b9", // Staging bundle ID
          buttonLabel: "Most Popular"
        },
        sixMonth: {
          months: 6,
          price: 860.00,
          pricePerMonth: 143.33,
          productId: isProduction 
            ? "d1f1479a-4750-4445-8273-ec894e37bdc6" // Production product ID
            : "d1f1479a-4750-4445-8273-ec894e37bdc6", // Staging product ID
          productBundleId: isProduction 
            ? "eab056fb-570d-46a5-97fa-b7de9973744b" // Production bundle ID
            : "eab056fb-570d-46a5-97fa-b7de9973744b" // Staging bundle ID
        },
        annual: {
          months: 12,
          price: 1076.00,
          pricePerMonth: 89.67,
          productId: isProduction 
            ? "d1f1479a-4750-4445-8273-ec894e37bdc6" // Production product ID
            : "d1f1479a-4750-4445-8273-ec894e37bdc6", // Staging product ID
          productBundleId: isProduction 
            ? "85599ee0-cdbc-48ad-b9aa-22f51bb45e94" // Production bundle ID
            : "0ef04963-94e6-4999-8d6e-8e4bee8a5920", // Staging bundle ID
          buttonLabel: "Best Deal"
        }
      }
    },
    product_2: {
      id: "product_2",
      name: "Semaglutide Tablets",
      display_name: "Semaglutide",
      another_name: "Semaglutide dissolvable tablets",
      type: "tablet",
      medication: "semaglutide",
      isVisible: false,
      price: 327.00,
      productId: isProduction 
        ? "8bf95e1a-dafd-4ae4-9f0e-9c420c4b3c45" // Production product ID
        : "8bf95e1a-dafd-4ae4-9f0e-9c420c4b3c45", // Staging product ID
      productBundleId: isProduction 
        ? "8bf95e1a-dafd-4ae4-9f0e-9c420c4b3c45" // Production bundle ID
        : "8bf95e1a-dafd-4ae4-9f0e-9c420c4b3c45", // Staging bundle ID
      imageUrl: "/images/products/semaglutide-tablets.png",
      imageUrl2: "/images/products/semaglutide-tablets.png",
      pricePlan: {
        monthly: {
          months: 1,
          price: 327.00,
          pricePerMonth: 327.00,
          productId: isProduction 
            ? "8bf95e1a-dafd-4ae4-9f0e-9c420c4b3c45" // Production product ID
            : "8bf95e1a-dafd-4ae4-9f0e-9c420c4b3c45", // Staging product ID
          productBundleId: isProduction 
            ? "8bf95e1a-dafd-4ae4-9f0e-9c420c4b3c45" // Production bundle ID
            : "8bf95e1a-dafd-4ae4-9f0e-9c420c4b3c45" // Staging bundle ID
        },
        quarterly: {
          months: 3,
          price: 891.00,
          pricePerMonth: 297.00,
          productId: isProduction 
            ? "8bf95e1a-dafd-4ae4-9f0e-9c420c4b3c45" // Production product ID
            : "8bf95e1a-dafd-4ae4-9f0e-9c420c4b3c45", // Staging product ID
          productBundleId: isProduction 
            ? "e47d2f29-60fd-44a0-b0cb-ae69fa43ccf1" // Production bundle ID
            : "e47d2f29-60fd-44a0-b0cb-ae69fa43ccf1", // Staging bundle ID
          buttonLabel: "Most Popular"
        },
        sixMonth: {
          months: 6,
          price: 1602.00,
          pricePerMonth: 267.00,
          productId: isProduction 
            ? "8bf95e1a-dafd-4ae4-9f0e-9c420c4b3c45" // Production product ID
            : "8bf95e1a-dafd-4ae4-9f0e-9c420c4b3c45", // Staging product ID
          productBundleId: isProduction 
            ? "ca17cad0-4a6b-48d8-a406-da323b729069" // Production bundle ID
            : "ca17cad0-4a6b-48d8-a406-da323b729069" // Staging bundle ID
        },
        annual: {
          months: 12,
          price: 2844.00,
          pricePerMonth: 237.00,
          productId: isProduction 
            ? "8bf95e1a-dafd-4ae4-9f0e-9c420c4b3c45" // Production product ID
            : "8bf95e1a-dafd-4ae4-9f0e-9c420c4b3c45", // Staging product ID
          productBundleId: isProduction 
            ? "4ae41c3d-9171-4b91-b9ca-582989713d01" // Production bundle ID
            : "4ae41c3d-9171-4b91-b9ca-582989713d01", // Staging bundle ID
          buttonLabel: "Best Deal"
        }
      }
    },
    product_3: {
      id: "product_3",
      name: "Tirzepatide Injection",
      display_name: "Tirzepatide",
      another_name: "Tirzepatide Injection",
      type: "injection",
      medication: "tirzepatide",
      isVisible: true,
      price: 389.00,
      productId: isProduction 
        ? "3769128e-de74-453b-82f5-0827207dfbdb" // Production product ID
        : "7c2c4037-ea73-40a9-abf6-39e751e25007", // Staging product ID
      productBundleId: isProduction 
        ? "ba22499c-fe5e-4ea0-a10e-166c295e56a0" // Production bundle ID
        : "ba22499c-fe5e-4ea0-a10e-166c295e56a0", // Staging bundle ID
      imageUrl: "/images/products/tirzeptide_bottle-01x01.png",
      imageUrl2: "/images/products/tirzepatide-plan-v2.png",
      pricePlan: {
        monthly: {
          months: 1,
          price: 389.00,
          pricePerMonth: 389.00,
          productId: isProduction 
            ? "3769128e-de74-453b-82f5-0827207dfbdb" // Production product ID
            : "7c2c4037-ea73-40a9-abf6-39e751e25007", // Staging product ID
          productBundleId: isProduction 
            ? "ba22499c-fe5e-4ea0-a10e-166c295e56a0" // Production bundle ID
            : "ba22499c-fe5e-4ea0-a10e-166c295e56a0" // Staging bundle ID
        },
        quarterly: {
          months: 3,
          price: 767.00,
          pricePerMonth: 255.67,
          productId: isProduction 
            ? "3769128e-de74-453b-82f5-0827207dfbdb" // Production product ID
            : "7c2c4037-ea73-40a9-abf6-39e751e25007", // Staging product ID
          productBundleId: isProduction 
            ? "443c5ccb-8224-449d-9764-f36cbc88a7c7" // Production bundle ID
            : "443c5ccb-8224-449d-9764-f36cbc88a7c7", // Staging bundle ID
          buttonLabel: "Most Popular"
        },
        sixMonth: {
          months: 6,
          price: 1274.00,
          pricePerMonth: 212.33,
          productId: isProduction 
            ? "3769128e-de74-453b-82f5-0827207dfbdb" // Production product ID
            : "7c2c4037-ea73-40a9-abf6-39e751e25007", // Staging product ID
          productBundleId: isProduction 
            ? "ee2319a9-c75c-496a-a3f7-af8e5a3d85a7" // Production bundle ID
            : "619ceb3c-ef4a-4bf1-b342-c38d9e57fa74" // Staging bundle ID
        },
        annual: {
          months: 12,
          price: 1808.00,
          pricePerMonth: 150.67,
          productId: isProduction 
            ? "3769128e-de74-453b-82f5-0827207dfbdb" // Production product ID
            : "7c2c4037-ea73-40a9-abf6-39e751e25007", // Staging product ID
          productBundleId: isProduction 
            ? "178d1f07-bde7-4581-a905-91fb9c0f6d42" // Production bundle ID
            : "178d1f07-bde7-4581-a905-91fb9c0f6d42", // Staging bundle ID
          buttonLabel: "Best Deal"
        }
      }
    },
    product_4: {
      id: "product_4",
      name: "Tirzepatide Tablets",
      display_name: "Tirzepatide",
      another_name: "Tirzepatide dissolvable tablets",
      type: "tablet",
      medication: "tirzepatide",
      isVisible: false,
      price: 369.00,
      productId: isProduction 
        ? "fc82246f-005d-4b17-9a01-f5dc7e975fe8" // Production product ID
        : "fc82246f-005d-4b17-9a01-f5dc7e975fe8", // Staging product ID
      productBundleId: isProduction 
        ? "fc82246f-005d-4b17-9a01-f5dc7e975fe8" // Production bundle ID
        : "fc82246f-005d-4b17-9a01-f5dc7e975fe8", // Staging bundle ID
      imageUrl: "/images/products/tirzepatide-tablets.png",
      imageUrl2: "/images/products/tirzepatide-tablets.png",
      pricePlan: {
        monthly: {
          months: 1,
          price: 369.00,
          pricePerMonth: 369.00,
          productBundleId: isProduction 
            ? "fc82246f-005d-4b17-9a01-f5dc7e975fe8" // Production bundle ID
            : "fc82246f-005d-4b17-9a01-f5dc7e975fe8" // Staging bundle ID
        },
        quarterly: {
          months: 3,
          price: 1017.00,
          pricePerMonth: 339.00,
          productBundleId: isProduction 
            ? "f4ff318d-4244-4fb1-b3b9-2e9e41982087" // Production bundle ID
            : "f4ff318d-4244-4fb1-b3b9-2e9e41982087", // Staging bundle ID
          buttonLabel: "Most Popular"
        },
        sixMonth: {
          months: 6,
          price: 1854.00,
          pricePerMonth: 309.00,
          productBundleId: isProduction 
            ? "fa098e2d-065c-48da-bb4d-8f9fb8206e23" // Production bundle ID
            : "fa098e2d-065c-48da-bb4d-8f9fb8206e23" // Staging bundle ID
        },
        annual: {
          months: 12,
          price: 3348.00,
          pricePerMonth: 279.00,
          productBundleId: isProduction 
            ? "dce4e1d2-d317-4143-bdd2-6fb48357855c" // Production bundle ID
            : "dce4e1d2-d317-4143-bdd2-6fb48357855c", // Staging bundle ID
          buttonLabel: "Best Deal"
        }
      }
    }
  }
}

// Default export for backward compatibility with JSON import syntax
export default getCvProductsConfig()
